import React from 'react';
import type { IncomeExpenseReportData, MessageButton } from '../../../../types/chat-message';
import { ReportActionButtons } from './ReportActionButtons';
import { ReportTitle } from './ReportTitle';
import './RevenueReport.scss';

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('vi-VN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount ?? 0) + 'đ';
};

const formatDiff = (amount: number): string => {
  if (amount > 0) return `+${formatCurrency(amount)}`;
  if (amount < 0) return `-${formatCurrency(Math.abs(amount))}`;
  return formatCurrency(0);
};

const getDiffClass = (amount: number): string => {
  if (amount > 0) return 'positive';
  if (amount < 0) return 'negative';
  return 'neutral';
};

interface IncomeExpenseReportProps {
  data?: IncomeExpenseReportData;
  buttons?: MessageButton[];
  onButtonClick?: (name: string, id: number, isHidden?: boolean) => void;
  isLastMessage?: boolean;
  isFromHistory?: boolean;
  text?: string;
}

const MetricItem: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="rr-metric-row">
    <span className="rr-metric-label">{label}</span>
    <span className="rr-metric-value">{value}</span>
  </div>
);

const DiffMetricItem: React.FC<{ remindCash: number }> = ({ remindCash }) => (
  <div className="rr-metric-row">
    <span className="rr-metric-label">Chênh lệch</span>
    <span className={`rr-metric-value ${getDiffClass(remindCash)}`}>
      {formatDiff(remindCash)}
    </span>
  </div>
);

const IncomeExpenseCard: React.FC<{
  totalCashIn: number;
  totalCashOut: number;
  remindCash: number;
}> = ({ totalCashIn, totalCashOut, remindCash }) => (
  <div className="rr-card">
    <div className="rr-metrics">
      <MetricItem label="Tổng thu" value={formatCurrency(totalCashIn)} />
      <MetricItem label="Tổng chi" value={formatCurrency(totalCashOut)} />
      <div className="rr-divider" />
      <DiffMetricItem remindCash={remindCash} />
    </div>
  </div>
);

const ReportBody: React.FC<{
  data?: IncomeExpenseReportData;
  text?: string;
}> = ({ data, text }) => {
  const { timeRange, totalCashIn = 0, totalCashOut = 0, remindCash = 0 } = data ?? {};
  if (!data || (totalCashIn === 0 && totalCashOut === 0)) {
    return <span className="rr-empty-text">{text || 'Không có dữ liệu cho kỳ báo cáo này'}</span>;
  }
  const title = text || `BÁO CÁO THU CHI ${timeRange || ''}`;
  return (
    <>
      <div className="rr-header"><ReportTitle text={title} /></div>
      <IncomeExpenseCard totalCashIn={totalCashIn} totalCashOut={totalCashOut} remindCash={remindCash} />
    </>
  );
};

export const IncomeExpenseReport: React.FC<IncomeExpenseReportProps> = (props) => {
  const { data, buttons, onButtonClick, isLastMessage, isFromHistory, text } = props;
  const isButtonsDisabled = !isLastMessage || !!isFromHistory;
  return (
    <div className="revenue-report income-expense-report">
      <ReportBody data={data} text={text} />
      <ReportActionButtons
        buttons={buttons}
        isDisabled={isButtonsDisabled}
        onButtonClick={onButtonClick}
        reportType="Báo cáo thu chi"
        suggestionTitle="Bạn có muốn xem thời gian khác không?"
      />
    </div>
  );
};
